import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cat from "@/game/Cat";
import sfx from "@/game/sfx";
import { useTr } from "@/autoTranslate";

/**
 * Milkoş Runs — Chrome Dino style endless runner.
 * Space / Up / Tap-cat to jump. Avoid falling yarn balls.
 */
const GROUND = 200;
const CAT_SIZE = 60;
const CAT_X = 60;
const GRAVITY = 2200;
const JUMP_V = 780;

export default function MilosRuns({ onExit }) {
  const tr = useTr();
  const [best, setBest] = useState(() =>
    parseInt(localStorage.getItem("milos_best") || "0", 10),
  );
  const [score, setScore] = useState(0);
  const [state, setState] = useState("idle"); // idle | playing | over
  const [catY, setCatY] = useState(0);
  const [obs, setObs] = useState([]);
  const obsRef = useRef([]);
  const speedRef = useRef(360);
  const catVyRef = useRef(0);
  const catYRef = useRef(0);
  const rafRef = useRef();
  const lastTsRef = useRef(0);
  const spawnTRef = useRef(0);
  const scoreTRef = useRef(0);
  const stageW = 640;

  const scoreRef = useRef(0);

  const jump = () => {
    if (state !== "playing") return;
    if (catYRef.current <= 0.1) catVyRef.current = JUMP_V;
  };

  const start = () => {
    setObs([]);
    obsRef.current = [];
    setScore(0);
    scoreRef.current = 0;
    catVyRef.current = 0;
    catYRef.current = 0;
    setCatY(0);
    speedRef.current = 360;
    spawnTRef.current = 0;
    scoreTRef.current = 0;
    setState("playing");
  };

  const stop = (final) => {
    setState("over");
    sfx.sad();
    if (final > best) {
      setBest(final);
      localStorage.setItem("milos_best", String(final));
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "ArrowUp") {
        e.preventDefault();
        if (state === "playing") jump();
        else if (state === "idle" || state === "over") start();
      }
      if (e.code === "Escape") onExit && onExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onExit]);

  useEffect(() => {
    if (state !== "playing") return;
    lastTsRef.current = 0;
    let alive = true;
    const loop = (ts) => {
      if (!alive) return;
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      catVyRef.current -= GRAVITY * dt;
      catYRef.current = Math.max(0, catYRef.current + catVyRef.current * dt);
      if (catYRef.current === 0) catVyRef.current = 0;
      setCatY(catYRef.current);

      const speed = speedRef.current;
      // Move + cull obstacles (kept in a ref so collision reads live data)
      obsRef.current = obsRef.current
        .map((o) => ({ ...o, x: o.x - speed * dt }))
        .filter((o) => o.x > -60);

      spawnTRef.current += dt;
      const currentScore = scoreRef.current;
      const spawnEvery = Math.max(0.9, 1.6 - currentScore / 500);
      if (spawnTRef.current > spawnEvery) {
        spawnTRef.current = 0;
        const isBird = Math.random() < 0.28 && currentScore > 30;
        obsRef.current.push({
          id: `${Date.now()}-${Math.random()}`,
          x: stageW + 20,
          y: isBird ? 80 : 0,
          w: isBird ? 28 : 22,
          h: isBird ? 20 : 26,
          bird: isBird,
        });
      }
      setObs(obsRef.current.slice());

      scoreTRef.current += dt;
      if (scoreTRef.current > 0.06) {
        scoreTRef.current = 0;
        scoreRef.current += 1;
        setScore(scoreRef.current);
      }
      speedRef.current = Math.min(720, 360 + scoreRef.current * 1.2);

      const catBox = {
        x: CAT_X,
        y: catYRef.current,
        w: CAT_SIZE - 10,
        h: CAT_SIZE - 10,
      };
      let hit = false;
      for (const o of obsRef.current) {
        if (
          catBox.x < o.x + o.w &&
          catBox.x + catBox.w > o.x &&
          catBox.y < o.y + o.h &&
          catBox.y + catBox.h > o.y
        ) {
          hit = true;
          break;
        }
      }
      if (hit) {
        alive = false;
        stop(scoreRef.current);
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      alive = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [state]);

  return (
    <div className="w-full" data-testid="minigame-milos-runs" data-minigame-active="true">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Kedi oyunu")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr("Milkoş Koşuyor")}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Rekor")} · {best}
          </div>
          <div className="display-serif italic text-3xl">{score}</div>
        </div>
      </div>

      <div className="mt-3 rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--bone-soft)] p-3 text-xs md:text-sm" data-testid="howto-runs">
        <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">{tr("Nasıl oynanır")}</div>
        <p className="mt-1">{tr("Boşluk tuşuna ya da yukarı oka bas (veya sahneye tıkla) ve zıpla. Gelen yumak ve kuşlara çarpma; ne kadar uzun dayanırsan skorun o kadar artar.")}</p>
      </div>

      <motion.div
        className="mt-6 relative mx-auto rounded-md border-2 border-[color:var(--ink)] overflow-hidden"
        animate={state === "over" ? { x: [0, -10, 9, -7, 6, 0] } : { x: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: stageW, maxWidth: "100%", height: GROUND + 80, background: "var(--pink)" }}
        onClick={() => {
          if (state === "playing") jump();
          else start();
        }}
      >
        {/* Clouds */}
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/70"
            style={{
              left: (i * 220 + score * 0.3) % (stageW + 100) - 80,
              top: 30 + i * 12,
              width: 60,
              height: 20,
            }}
          />
        ))}
        {/* Ground */}
        <div
          className="absolute left-0 right-0 border-t-2 border-[color:var(--ink)]"
          style={{ top: GROUND + 40, background: "var(--peach)", height: 40 }}
        />
        {/* Cat */}
        <div
          className="absolute"
          style={{
            left: CAT_X,
            bottom: 40 + catY,
            width: CAT_SIZE,
            height: CAT_SIZE,
            transition: "transform 0.05s",
          }}
        >
          <Cat dir={1} sitting={catY < 1} size={CAT_SIZE} />
        </div>
        {/* Obstacles */}
        {obs.map((o) => (
          <div
            key={o.id}
            className="absolute rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center"
            style={{
              left: o.x,
              bottom: 40 + o.y,
              width: o.w,
              height: o.h,
              background: o.bird ? "var(--lavender)" : "var(--pink-hot)",
              fontSize: 12,
              color: "var(--ink)",
            }}
          >
            {o.bird ? "🕊" : "🧶"}
          </div>
        ))}
        {/* Overlays */}
        {state === "idle" && (
          <div className="absolute inset-0 flex items-center justify-center flex-col bg-[rgba(255,241,230,0.86)]">
            <div className="display-serif italic text-4xl">{tr("Hazır mısın, Milkoş?")}</div>
            <div className="mt-3 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("Zıplamak için buraya tıkla ya da Boşluk'a bas")}
            </div>
            <button
              onClick={start}
              className="btn-pill warm mt-6"
              data-testid="milos-runs-start"
            >
              {tr("Koşmaya başla →")}
            </button>
          </div>
        )}
        {state === "over" && (
          <div className="absolute inset-0 flex items-center justify-center flex-col bg-[rgba(255,93,143,0.35)]">
            <div className="display-serif italic text-4xl">{tr("Eyvah")} — {score}</div>
            <div className="mt-2 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("Rekor")}: {best}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={start}
                className="btn-pill warm"
                data-testid="milos-runs-retry"
              >
                {tr("Tekrar ↻")}
              </button>
              <button
                onClick={onExit}
                className="btn-pill ghost"
                data-testid="milos-runs-exit"
              >
                {tr("Çık")}
              </button>
            </div>
          </div>
        )}
      </motion.div>

      <div className="mt-4 flex items-center gap-2 text-sm font-mono-label text-[10px] uppercase tracking-widest opacity-70">
        <span className="kbd">{tr("Boşluk")}</span>
        <span>{tr("ya da")}</span>
        <span className="kbd">↑</span>
        <span>{tr("ile zıpla · Milkoş yumaklardan kaçar")}</span>
      </div>
    </div>
  );
}
